import type { ContentDto, PageDto, WatchlistDto } from "$src/core/models/dto";
import type {
  CarCatalog,
  CarContentType,
  CarItem,
  CarSection,
} from "$src/core/services/car/types";

const MAX_ITEMS_PER_SECTION = 50;

/** EPG / live ("linear") is intentionally excluded from the car surface. */
const CAR_CONTENT_TYPES: CarContentType[] = ["movies", "series", "episodes"];

export const isCarPlayable = (content: ContentDto): boolean =>
  CAR_CONTENT_TYPES.includes(content.type);

export const pickImageUrl = (content: ContentDto): string | undefined => {
  const image = content.image ?? content.images;
  return (
    image?.landscape?.url ??
    image?.portrait?.url ??
    image?.square?.url ??
    image?.url ??
    undefined
  );
};

const getProgress = (content: ContentDto): number | undefined => {
  if ("progress" in content && typeof content.progress === "number") {
    return content.progress;
  }
  return undefined;
};

export const toCarItem = (content: ContentDto): CarItem => {
  const { subtitle } = content;
  const imageUrl = pickImageUrl(content);

  // Omit absent fields: the native Kotlin `Map` conversion rejects `undefined`.
  return {
    id: content.id,
    type: content.type,
    title: content.title ?? "",
    ...(subtitle !== undefined && { subtitle }),
    ...(imageUrl !== undefined && { imageUrl }),
  };
};

const dedupeById = (items: ContentDto[]): ContentDto[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

export type CarCatalogLabels = {
  continueWatching: string;
  watchlist: string;
};

const buildCarCatalog = (
  page: PageDto | undefined,
  watchlist: WatchlistDto | undefined,
  labels: CarCatalogLabels,
): CarCatalog => {
  const sections: CarSection[] = [];

  const allItems = page?.sections?.flatMap((section) => section.items) ?? [];

  const continueItems = dedupeById(
    allItems.filter((item) => {
      if (!isCarPlayable(item)) return false;
      const progress = getProgress(item);
      return progress !== undefined && progress > 0 && progress < 100;
    }),
  )
    .slice(0, MAX_ITEMS_PER_SECTION)
    .map(toCarItem);

  if (continueItems.length > 0) {
    sections.push({
      id: "continue-watching",
      title: labels.continueWatching,
      items: continueItems,
    });
  }

  const watchlistItems = (watchlist?.items ?? [])
    .filter(isCarPlayable)
    .slice(0, MAX_ITEMS_PER_SECTION)
    .map(toCarItem);

  if (watchlistItems.length > 0) {
    sections.push({
      id: "watchlist",
      title: labels.watchlist,
      items: watchlistItems,
    });
  }

  // Each section becomes a CarPlay tab; an untitled one renders as "More".
  for (const section of page?.sections ?? []) {
    const title = section.title?.trim();
    if (!title) continue;

    const items = section.items
      .filter(isCarPlayable)
      .slice(0, MAX_ITEMS_PER_SECTION)
      .map(toCarItem);

    if (items.length > 0) {
      sections.push({
        id: section.id,
        title,
        items,
      });
    }
  }

  return {
    sections,
    updatedAt: Date.now(),
  };
};

export default buildCarCatalog;
